/**
 * The controller page's Settings tab: name, farm, hardware facts, master pumps, firmware.
 * Split out of pages/irrigation/EspNowControllerDetail.tsx.
 */
import { RefreshCw, AlertCircle, Building2, Pencil, Upload, Cpu } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import {
  type EspNowController,
  type EspNowRouter,
  type MasterPump,
  formatDuration,
} from '../../../services/espnowIrrigation';
import { MasterPumpConfig } from './MasterPumpConfig';

// ─── Settings Tab ─────────────────────────────────────────────────────────────

export function SettingsTab({
  controller,
  farms,
  savingFarm,
  onFarmChange,
  editingName,
  nameValue,
  savingName,
  onStartRename,
  onCancelRename,
  onNameChange,
  onSaveName,
  isSuperAdmin,
  otaSending,
  onOtaMaster,
  masterPumps,
  routers,
  onAddPump,
  onUpdatePump,
  onDeletePump,
}: {
  controller: EspNowController;
  farms: Array<{ id: number; name: string }>;
  savingFarm: boolean;
  onFarmChange: (farmId: number | null) => void;
  editingName: boolean;
  nameValue: string;
  savingName: boolean;
  onStartRename: () => void;
  onCancelRename: () => void;
  onNameChange: (v: string) => void;
  onSaveName: () => void;
  isSuperAdmin: boolean;
  otaSending: string | null;
  onOtaMaster: () => void;
  masterPumps: MasterPump[];
  routers: EspNowRouter[];
  onAddPump: (routerMac: string, relayIndex: number, label: string) => void;
  onUpdatePump: (pumpId: string, data: { router_mac?: string; relay_index?: number; label?: string | null }) => void;
  onDeletePump: (pumpId: string) => void;
}) {
  return (
    <div className="space-y-4">
      {/* Controller name */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Pencil className="w-4 h-4 text-blue-500" /> Controller Name
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editingName ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                autoFocus
                value={nameValue}
                onChange={e => onNameChange(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') onSaveName(); if (e.key === 'Escape') onCancelRename(); }}
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Button size="sm" onClick={onSaveName} disabled={savingName}>
                {savingName ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={onCancelRename}>Cancel</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-800">
                  {controller.controller_name || <span className="italic text-gray-400">(no name set)</span>}
                </div>
                <div className="text-xs text-gray-400 font-mono mt-0.5">{controller.controller_id}</div>
              </div>
              <Button variant="outline" size="sm" onClick={onStartRename}>Rename</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Farm assignment */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4 text-green-600" /> Farm Assignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={controller.farm_id ?? ''}
              onChange={e => onFarmChange(e.target.value ? Number(e.target.value) : null)}
              disabled={savingFarm || farms.length === 0}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 disabled:bg-gray-50"
            >
              <option value="">No farm assigned</option>
              {farms.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {savingFarm && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {farms.length === 0
              ? 'No farms defined. Create one under Farms first.'
              : 'Linking to a farm groups this controller with others on the same property.'}
          </p>
        </CardContent>
      </Card>

      {/* Hardware — what the master reported about itself at registration */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Cpu className="w-4 h-4 text-slate-500" /> Hardware
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
            <HardwareFact label="Device type" value={controller.device_type ?? '—'} />
            <HardwareFact label="MAC" value={controller.mac ?? '—'} mono />
            <HardwareFact label="Last IP" value={controller.ip ?? '—'} mono />
            <HardwareFact
              label="WiFi channel"
              value={controller.wifi_channel != null ? String(controller.wifi_channel) : '—'}
            />
            <HardwareFact
              label="ESP-NOW channel"
              value={controller.espnow_channel != null ? String(controller.espnow_channel) : '—'}
              // A mismatch means the mesh cannot form; the firmware only logs it to serial.
              warn={
                controller.espnow_channel != null &&
                controller.wifi_channel != null &&
                controller.espnow_channel !== controller.wifi_channel
              }
              warnText="Does not match the WiFi channel — the mesh will not form."
            />
            <HardwareFact
              label="Clock offset"
              value={
                controller.timezone_offset === 0
                  ? 'UTC'
                  : `UTC${controller.timezone_offset > 0 ? '+' : '−'}${formatDuration(Math.abs(controller.timezone_offset))}`
              }
            />
          </dl>
          <div className="mt-4 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">On-board I/O</p>
            <div className="flex flex-wrap gap-1.5">
              <CapabilityChip label="Relays" present={controller.has_local_relays} />
              <CapabilityChip label="Digital inputs" present={controller.has_local_inputs} />
              <CapabilityChip label="Analog inputs" present={controller.has_local_analog} />
            </div>
            {controller.has_local_relays && (
              <p className="text-xs text-gray-400 mt-2">
                The master's own relays report alongside the routers — look for{' '}
                {controller.mac ? <span className="font-mono">{controller.mac}</span> : 'its own MAC'} on the Routers tab.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Master pumps */}
      <MasterPumpConfig
        masterPumps={masterPumps}
        routers={routers}
        onAdd={onAddPump}
        onUpdate={onUpdatePump}
        onDelete={onDeletePump}
      />

      {/* OTA (super admin only).
          Never on an XBee site: "master firmware" is an ESP32 image queued for
          a device to fetch on its next command poll, and an XBee site has no
          such device - its coordinator is a laptop running the Python bridge,
          which updates itself through BridgeUpdatePanel at the top of this tab.
          Shown here the button was a dead control that looked like the right
          one, sitting directly below the one that actually works. */}
      {isSuperAdmin && controller.transport !== 'xbee' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Upload className="w-4 h-4 text-gray-500" /> Firmware
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-700">
                  Master firmware{' '}
                  {controller.firmware_version != null && (
                    <span className="text-xs text-gray-400">· v{controller.firmware_version}</span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Router firmware updates are available from each router card on the Routers tab.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onOtaMaster}
                disabled={otaSending !== null || !controller.online}
                title={!controller.online ? 'Controller is offline' : 'Queue an OTA firmware update for the master'}
              >
                <Upload className={`w-4 h-4 sm:mr-1 ${otaSending === 'master' ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">
                  {otaSending === 'master' ? 'Queuing…' : 'Update Master'}
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function HardwareFact({
  label,
  value,
  mono,
  warn,
  warnText,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warn?: boolean;
  warnText?: string;
}) {
  return (
    <div>
      <dt className="text-xs text-gray-400">{label}</dt>
      <dd
        className={`mt-0.5 ${mono ? 'font-mono text-xs' : 'text-sm'} ${
          warn ? 'text-amber-600 font-medium' : 'text-gray-800'
        }`}
        title={warn ? warnText : undefined}
      >
        {value}
        {warn && <AlertCircle className="w-3.5 h-3.5 inline ml-1 -mt-0.5" />}
      </dd>
    </div>
  );
}

function CapabilityChip({ label, present }: { label: string; present: boolean | null }) {
  // null means the master has not reported this yet — not the same as "absent".
  if (present == null) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-50 border border-gray-200 text-gray-400">
        {label} · unknown
      </span>
    );
  }
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
        present
          ? 'bg-green-50 border border-green-200 text-green-700'
          : 'bg-gray-50 border border-gray-200 text-gray-400'
      }`}
    >
      {label} {present ? '✓' : '—'}
    </span>
  );
}
